import * as React from "react";

import "./mailing-modules.scss";

import {enableCanadaMailing}    from "@Common/logic/enable-canada-mailing";
import {cs}                     from "@Common/react/chain-services";
import {SampleMailingCardLogic} from "@Common/ui-components/heavy/item-options/modules/sample-mailing-card/sample-mailing-card-logic";
import {formatMoney1}           from "@Common/ui-components/language/format-money";
import {formatNumberBig}        from "@Common/ui-components/language/format-number-big";
import {gnv, snv}               from "@Common/utils/arr-path";
import {chain}                  from "@Common/utils/fs";

import {getPurchaseMlSummaryLines} from "@Xpresso/routes/common/address-lists/get-purchase-ml-summary-lines";
import {MailingOptions}            from "@Xpresso/routes/common/heavy/mailing-options/mailing-options";
import {SampleMailingCard}         from "@Xpresso/routes/common/heavy/sample-mailing-card/sample-mailing-card";

import {
    enrollmentEditor, summaryLines,
    ui
} from "../common/expo-types";

import {ScMailingClassLogic}      from "./sc-mailing-class-logic";
import {UnaddressedCopiesModules} from "./unaddressed-copies/unaddressed-copies-modules";

export const MailingModules = ({initEnrollment, templateOptions}) => [

    {
        name: "mailingClassLogic",
        load: ({mailingLists, addressLists1}, next) => ScMailingClassLogic({
            initEnrollment,
            templateOptions,
            mailingLists,
            mailingListIds: addressLists1.mailingListIds,
            next,
        }),
        expo: [enrollmentEditor],
        requires: ["mailingLists", "addressLists1"],
    },

    {
        name: "mailingClassId",
        load: ({mailingClassLogic}, next) => next(
            mailingClassLogic.ui.state.value
        ),
        requires: ["mailingClassLogic"],
    },

    {
        load: ({renderAddressLists, mailingClassLogic, addressLists2, method}, next) => cs(

            ["canadaMailing", (_, next) => enableCanadaMailing({templateOptions, next})],

            ({canadaMailing}) => next(method === "mailing" && {
                enrollmentEditor: addressLists2.enrollmentEditor,
                ui: ({showErrors}) => (
                    <div className="mailing-modules-52k">

                        <div className="header">
                            <div {...{
                                className: "title",

                                "xd-qa-type": "label",
                                "xd-qa-name": "Mailing",
                            }}>
                                Mailing
                            </div>
                            <div {...{
                                className: "subtitle",
                                "xd-qa-type": "text",
                                "xd-qa-name": "Mailing Description",
                            }}>
                                Select address list(s), mailing service level, and set up your preferences
                            </div>
                        </div>

                        {MailingOptions({
                            mailingClass: {
                                state: mailingClassLogic.ui.state,
                                forcedStandard: mailingClassLogic.ui.forcedStandard,
                                mailingClassKeys: mailingClassLogic.ui.mailingClassKeys,
                                hasEddm: false,
                                canadaMailing,
                            },

                            renderAddressLists: () => renderAddressLists({
                                showErrors,
                                className: "section address-lists-6rk",
                            }),
                        })}
                    </div>
                ),
            }),
        ),
        expo: [ui(1), enrollmentEditor],
        requires: ["renderAddressLists", "mailingClassLogic", "method", "addressLists2"],
    },

    {
        name: "sampleMailingCardLogic",
        load: ({method}, next) => cs(
            ["logic", (_, next) => SampleMailingCardLogic({
                initSelected: gnv(initEnrollment, ["distribution_options", "mailing", "sample_card"]),
                next,
            })],
            ({logic}) => next(method === "mailing" && {
                selected: logic.ui.selected.value,
                ui: () => SampleMailingCard({
                    ...logic.ui,
                    disclaimer: "The Sample Card will be charged at the same price per piece as the Edition.",
                    disablePrepopulateEditModal: true,
                }),
                enrollmentEditor: {
                    ...logic.editor,

                    prepareSave: (enrollment) => {
                        const {onSaved, selected} = logic.editor.prepareSave();
                        return ({
                            newValue: snv(
                                enrollment, ["distribution_options", "mailing", "sample_card"],
                                !!selected,
                            ),
                            onSaved,
                        });
                    },
                },
            }),
        ),
        expo: [ui(5, {hasSpacer: true}), enrollmentEditor,],
        requires: ["method"],
    },

    {
        load: (
            {
                method, mailingClassId, mailingLists, addressLists1, unaddressedCopiesLogic, shippingServiceLevel1, shippingAddress1,
                mailedQuantity, shippedQuantity, getMailingPaperPrice, getShippingPaperPrice
            },
            next
        ) => next(method === "mailing" && (({templateOptionsList}) => [
            {
                name: "Delivery Method",
                value: unaddressedCopiesLogic.state.value ? "Mailed + Shipped" : "Mailed",
            },
            {
                name: "Service Level",
                value: [
                    (mailingClassId === "1" ? "First Class" : (mailingClassId === "5" ? "Neighborhood Direct Mail" : "Standard Mailing") ),
                    unaddressedCopiesLogic.state.value && shippingServiceLevel1.serviceLevelLabel,
                ].filter(v=>v).join(" + "),
            },
            {
                name: "Mailing List(s)",
                value: addressLists1.state.value?.map((id) => mailingLists.value?.find((ml) => ml.id == id)?.mailingListName).join(", "),
            },
            {
                name: "Shipping Address",
                value: unaddressedCopiesLogic.state.value && shippingAddress1.address && (
                    chain(
                        shippingAddress1.address,
                        (_) => `${_.address1}, ${_.city} ${_.state} ${_.postalCode}`,
                    )
                ) || "N/A",
            },
            {
                name: "Quantity per Edition",
                value: [
                    formatNumberBig(mailedQuantity) + " (Mailed)",
                    unaddressedCopiesLogic.state.value && (
                        formatNumberBig(shippedQuantity) + " (Shipped)"
                    ),
                ].filter(v=>v).join(" & "),
            },
            ...getPurchaseMlSummaryLines({
                templateOptions,
                mailingLists: addressLists1.state.value?.map((id) => mailingLists.value?.find((ml) => ml.id == id)).filter(v=>v),
                isMailing: true,
            })
                .map(({label, qty, unitPrice})=> ({
                    name: <>{label} ({formatMoney1(unitPrice)} each)</>,
                    value: formatMoney1(unitPrice*qty),
                }))
            ,
            // ...templateOptionsList?.map((templateOptions) => {
            //     const mailingPaperPrice = getMailingPaperPrice(templateOptions);
            //     const shippingPaperPrice = getShippingPaperPrice(templateOptions);
            //     return ({
            //         name: `Price per Piece (${templateOptions?.product.name})`,
            //         value: joinJsx(
            //             [
            //                 fragments(!mailingPaperPrice ? "--" : formatMoney1(mailingPaperPrice.price), ` (Mailed)`),
            //                 unaddressedCopiesLogic.state.value && fragments(!shippingPaperPrice ? "--" : formatMoney1(shippingPaperPrice.price), ` (Shipped)`)
            //             ].filter(v=>v),
            //             () => " & ",
            //         ),
            //     });
            // })??[],
            // {
            //     name: "Price per Piece",
            //     value: joinJsx(
            //         [
            //             fragments(!mailingPaperPrice ? "--" : formatMoney1(mailingPaperPrice.price), ` (Mailed)`),
            //             unaddressedCopiesLogic.state.value && fragments(!shippingPaperPrice ? "--" : formatMoney1(shippingPaperPrice.price), ` (Shipped)`)
            //         ].filter(v=>v),
            //         () => " & ",
            //     ),
            // },
        ])),
        requires: [
            "method", "mailingClassId", "mailingLists", "addressLists1", "unaddressedCopiesLogic", "shippingServiceLevel1", "shippingAddress1",
            "mailedQuantity", "shippedQuantity", "getMailingPaperPrice", "getShippingPaperPrice",
        ],
        expo: [summaryLines],
    },

    ...UnaddressedCopiesModules({initEnrollment}),
];
